import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    input,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { StandoutClassroomService } from "../services/standout.classroom.service";
import { S25Util } from "../util/s25-util";
import { S25Const } from "../util/s25-const";

@Component({
    selector: "s25-chat",
    template: `
        @if (iframeUrl) {
            <div class="chat-wrapper" [style.width]="width()" [style.height]="height()">
                <iframe
                    [src]="iframeUrl"
                    [style.width]="width()"
                    [style.height]="height()"
                    frameborder="0"
                    allow="microphone; camera; clipboard-read; clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    (error)="onIframeError()"
                ></iframe>
                @if (showFallback) {
                    <div class="chat-fallback" [style.width]="width()" [style.height]="height()">
                        <div class="fallback-content">
                            <p>Chat cannot be embedded due to security restrictions.</p>
                            <a [href]="getChatLink()" target="_blank" class="btn btn-primary"> Open Chat </a>
                        </div>
                    </div>
                }
            </div>
        } @else {
            <div class="chat-loading" [style.width]="width()" [style.height]="height()">
                <span>Loading chat...</span>
            </div>
        }
    `,
    styles: [
        `
            .chat-wrapper {
                border: 1px solid #ddd;
                border-radius: 4px;
                overflow: hidden;
            }

            .chat-loading {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #666;
                font-style: italic;
            }

            .chat-fallback {
                position: absolute;
                top: 0;
                left: 0;
                background: rgba(255, 255, 255, 0.95);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10;
            }

            .fallback-content {
                text-align: center;
                padding: 20px;
            }

            .fallback-content p {
                margin-bottom: 15px;
                color: #666;
            }

            .btn {
                padding: 8px 16px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
            }

            .btn-primary {
                background-color: #007bff;
                color: white;
            }

            .btn-primary:hover {
                background-color: #0056b3;
            }

            iframe {
                border: none;
            }

            ::ng-deep .nm-party--on .chat-wrapper {
                border-color: rgba(255, 255, 255, 0.24);
            }

            ::ng-deep .nm-party--on .chat-loading {
                color: #ccc;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
})
export class S25ChatComponent implements OnInit, OnDestroy {
    // Optional channelId input using Angular signals
    channelId = input<string>();

    // Optional width and height inputs with units
    width = input<string>("100%");
    height = input<string>("600px");

    // iframe URL - updated when token refreshes
    iframeUrl: SafeResourceUrl | null = null;

    // Flag to show fallback UI when iframe fails
    showFallback: boolean = false;

    // Token refresh management
    private refreshTimeout: any = null;
    private readonly TOKEN_REFRESH_BUFFER_SECONDS = 60; // Refresh 1 minute before expiry

    constructor(
        private cd: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
    ) {}

    async ngOnInit() {
        await this.initializeChat();
    }

    private async initializeChat() {
        try {
            // Get JWT token from StandoutClassroomService
            const token = await StandoutClassroomService.getAuthToken();

            if (!token) {
                console.error("Failed to get JWT token for StandOut Classroom chat");
                S25Util.showError("Unable to authenticate with chat service");
                return;
            }

            // Build iframe URL with token
            this.buildIframeUrl(token);

            // Set up token refresh
            this.scheduleTokenRefresh(token);
        } catch (error) {
            console.error("Error initializing chat component:", error);
            S25Util.showError("Failed to load chat");
        } finally {
            this.cd.detectChanges();
        }
    }

    private buildIframeUrl(token: string) {
        const channel = this.channelId();
        let url: string = StandoutClassroomService.getBaseWsUrl() + "/iframe/chat";

        if (channel) {
            // For channel-specific chat
            url += `/${channel}/app?jwt=${encodeURIComponent(token)}`;
        } else {
            // For general chat app (no channel)
            url += `/app?jwt=${encodeURIComponent(token)}`;
        }

        // Sanitize the URL to prevent Angular security warnings
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    private scheduleTokenRefresh(token: string) {
        // Clear any existing timeout
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }

        try {
            // Parse JWT to get expiration time
            const tokenPayload = S25Util.parseJwt(token);
            if (!tokenPayload || !tokenPayload.exp) {
                console.warn("Could not parse JWT expiration, scheduling refresh in 30 minutes");
                this.refreshTimeout = setTimeout(() => this.refreshToken(), 30 * 60 * 1000);
                return;
            }

            const expirationTime = tokenPayload.exp * 1000; // Convert to milliseconds
            const currentTime = Date.now();
            const timeUntilExpiry = expirationTime - currentTime;
            const refreshTime = timeUntilExpiry - this.TOKEN_REFRESH_BUFFER_SECONDS * 1000;

            if (refreshTime > 0) {
                console.log(`Scheduling token refresh in ${Math.round(refreshTime / 1000)} seconds`);
                this.refreshTimeout = setTimeout(() => this.refreshToken(), refreshTime);
            } else {
                // Token is already expired or about to expire, refresh immediately
                console.log("Token expired or about to expire, refreshing immediately");
                this.refreshToken();
            }
        } catch (error) {
            console.error("Error parsing JWT token:", error);
            // Fallback: refresh in 30 minutes
            this.refreshTimeout = setTimeout(() => this.refreshToken(), 30 * 60 * 1000);
        }
    }

    private async refreshToken() {
        try {
            console.log("Refreshing JWT token...");

            // Get fresh token
            const newToken = await StandoutClassroomService.getAuthToken();

            if (!newToken) {
                console.error("Failed to refresh JWT token");
                S25Util.showError("Chat authentication expired");
                return;
            }

            // Update iframe URL with new token
            this.buildIframeUrl(newToken);

            // Schedule next refresh
            this.scheduleTokenRefresh(newToken);

            // Trigger change detection to update iframe
            this.cd.detectChanges();

            console.log("JWT token refreshed successfully");
        } catch (error) {
            console.error("Error refreshing JWT token:", error);
            S25Util.showError("Failed to refresh chat authentication");
        }
    }

    ngOnDestroy() {
        // Clear any pending token refresh timeout
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }
    }

    /**
     * Handle iframe loading errors (e.g., CSP violations)
     */
    onIframeError() {
        console.warn("Iframe failed to load, likely due to CSP restrictions");
        this.showFallback = true;
        this.cd.detectChanges();
    }

    /**
     * Get the proper chat link for opening in a new window/tab
     */
    getChatLink(): string {
        return StandoutClassroomService.getChatLink();
    }
}
